// src/store/slices/departmentsSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { departmentService, Department } from '../../services/departmentService';

interface DepartmentsState {
  departments: Department[];
  isLoading: boolean;
  error: string | null;
}

const initialState: DepartmentsState = {
  departments: [],
  isLoading: false,
  error: null,
};

export const fetchDepartments = createAsyncThunk(
  'departments/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const departments = await departmentService.getAllDepartments();
      return departments;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch departments');
    }
  }
);

export const createDepartment = createAsyncThunk(
  'departments/createDepartment',
  async (departmentData: { name: string }, { rejectWithValue }) => {
    try {
      const department = await departmentService.createDepartment(departmentData);
      return department;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create department');
    }
  }
);

export const updateDepartment = createAsyncThunk(
  'departments/updateDepartment',
  async ({ id, departmentData }: { id: string; departmentData: { name: string } }, { rejectWithValue }) => {
    try {
      const department = await departmentService.updateDepartment(id, departmentData);
      return department;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update department');
    }
  }
);

export const deleteDepartment = createAsyncThunk(
  'departments/deleteDepartment',
  async (id: string, { rejectWithValue }) => {
    try {
      await departmentService.deleteDepartment(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete department');
    }
  }
);

const departmentsSlice = createSlice({
  name: 'departments',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch departments cases - ONLY these should set the global error state
      .addCase(fetchDepartments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      // Create department cases - these should NOT set the global error state
      .addCase(createDepartment.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here - keep fetch errors visible
      })
      .addCase(createDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments.push(action.payload);
        // Don't modify error state
      })
      .addCase(createDepartment.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle create errors
      })
      // Update department cases - these should NOT set the global error state
      .addCase(updateDepartment.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        const { id, departmentData } = action.meta.arg;
        const index = state.departments.findIndex(dept => dept._id === id);
        if (index !== -1) {
          state.departments[index] = { ...state.departments[index], ...departmentData };
        }
        // Don't modify error state
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle update errors
      })
      // Delete department cases - these should NOT set the global error state
      .addCase(deleteDepartment.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments = state.departments.filter(dept => dept._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      });
  },
});

export const { clearError } = departmentsSlice.actions;
export default departmentsSlice.reducer;